import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Grid, Image } from 'semantic-ui-react'

import { GridRowColumn, Text } from '@/componentsV1/BaseComponents'
import { useReselector } from '@/utils/sharedHooks'
import { selectTaxListQuestionResponsesByQuestionIds } from '@/features/taxes/annual-taxes/tax-checklist/taxChecklist.selectors'
import { INCOME_LIABILITIES_SCREENS, IncomeAndLiabilitiesStepProps } from '.'
import { FormFlowFooter } from '@/components/form-flow/FormFlowFooter'
import ReviewSubSection from '@/features/taxes/annual-taxes/tax-checklist/shared/ReviewSubSection'
import { IncomeAndLiabilitiesQuestionIds } from './IncomeAndLiabilitiesPanel'
import { rentalPropertyQuestionIds } from './RentalPropertyPanel'
import { investmentsQuestionIds } from './InvestmentsPanel'
import { retirementContributedQuestionIds } from './RetirementContributedPanel'
import { lessCommonScenarioFollowUpQuestionIds } from './LessCommonScenariosPanel'
import { TaxListQuestionId } from '@/features/taxes/annual-taxes/tax-checklist/service'
import { useNavigateWithLocation } from '@/utils/routeHelpers'
import { selectTqFormIsReadOnly } from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import CommentArea from '../../../components/comment-area'
import { useReviewPanelComment } from '../../../helpers'
import { SubStepIdentifiers } from '../../shared/review-steps-and-progresses/stepProgress.helpers'
import { tqBigSpace } from '../../helpers'
import { outsideW2QuestionIds } from '@/features/taxes/annual-taxes/tax-checklist/form1040/income-and-liabilities/OutsideW2Panel'

const IncomeAndLiabilitiesReviewPanel = ({
  goBack,
}: IncomeAndLiabilitiesStepProps) => {
  const navigate = useNavigateWithLocation()
  const { formId } = useParams()
  const readOnly = useReselector(selectTqFormIsReadOnly, formId)

  const { comment, saveComment, updateComment } = useReviewPanelComment(
    SubStepIdentifiers.incomeAndLiabilities
  )

  const ownInvestmentsResponses = useReselector(
    selectTaxListQuestionResponsesByQuestionIds,
    [TaxListQuestionId.investments_owned],
    Number(formId)
  )
  const showInvestmentsSection = useMemo(
    () => ownInvestmentsResponses.some((response) => response?.value === true),
    [ownInvestmentsResponses]
  )

  return (
    <Grid>
      <GridRowColumn centerContent {...tqBigSpace}>
        <Image src="https://heard-images.s3.amazonaws.com/assets/magnifying-glass-checkmark.svg" />
      </GridRowColumn>
      <GridRowColumn {...tqBigSpace}>
        <Text as="display2" textAlign="center">
          Review
        </Text>
      </GridRowColumn>
      <GridRowColumn {...tqBigSpace}>
        <Text as="bodyLg">
          {readOnly
            ? 'You can no longer edit your Personal Tax Questionnaire responses, but you can view them anytime.'
            : 'Review your answers for accuracy. Click edit to go back to the previous section.'}
        </Text>
      </GridRowColumn>

      <Grid.Row />
      <ReviewSubSection
        title="income and liabilities"
        questionIds={IncomeAndLiabilitiesQuestionIds}
        editScreen={INCOME_LIABILITIES_SCREENS.incomeAndLiabilitiesIntro}
      />
      <ReviewSubSection
        title="W-2 income"
        questionIds={outsideW2QuestionIds}
        editScreen={INCOME_LIABILITIES_SCREENS.outsideW2s}
      />
      <ReviewSubSection
        title="rental property"
        questionIds={rentalPropertyQuestionIds}
        editScreen={INCOME_LIABILITIES_SCREENS.rentalProperty}
        group="groupId"
      />
      {showInvestmentsSection && (
        <ReviewSubSection
          title="investments"
          questionIds={investmentsQuestionIds}
          editScreen={INCOME_LIABILITIES_SCREENS.investments}
        />
      )}
      <ReviewSubSection
        title="retirement"
        questionIds={retirementContributedQuestionIds}
        editScreen={INCOME_LIABILITIES_SCREENS.retirementContributed}
        group="groupId"
      />

      <ReviewSubSection
        title="Less common scenarios"
        questionIds={lessCommonScenarioFollowUpQuestionIds}
        editScreen={INCOME_LIABILITIES_SCREENS.lessCommonScenarios}
      />
      {!readOnly && (
        <CommentArea
          label="Add a comment (optional)"
          placeholder="Add a note or ask a question to provide additional context for your tax preparer"
          value={comment}
          onChange={updateComment}
        />
      )}

      {!readOnly && (
        <FormFlowFooter
          isSubmit
          onBack={() => goBack(INCOME_LIABILITIES_SCREENS.lessCommonScenarios)}
          onForward={() => {
            saveComment()
            navigate('/taxes/annual/tax_checklist/')
          }}
        />
      )}
    </Grid>
  )
}

export default IncomeAndLiabilitiesReviewPanel
